import {
  getAddressAlignment,
  getAddressDisplayMode,
  getAddressPinBorderMode,
  getAddressPlacePosition,
  getAddressPlaceReference,
  getAddressPlaceVisible,
  getAddressTextPosition,
  getFlexJustifyValue,
  getTextAlignValue,
} from "../utils/layoutUtils";

function isVertical(position) {
  return position === "top" || position === "bottom";
}

function makePositionedGroup(primary, secondary, position, gap, groupStyle) {
  if (!primary) return secondary;
  if (!secondary) return primary;

  const secondaryFirst = position === "top" || position === "left";

  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: isVertical(position) ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        gap,
        minWidth: 0,
        ...groupStyle,
      }}
    >
      {secondaryFirst ? secondary : primary}
      {secondaryFirst ? primary : secondary}
    </span>
  );
}

export default function EventAddressLink({
  layoutConfig,
  mapsUrl,
  endereco,
  nomeEspaco,
  color,
  fontSize = 14,
  fontWeight = 500,
  iconSize = 18,
  gap = 12,
  defaultMode = "pinText",
  defaultAlign = "center",
  defaultPinBg = "#ffa726",
  defaultPinBorderMode = "none",
  defaultPinBorderColor = null,
  pinSize = 46,
  pinRadius = 14,
  style,
  textStyle,
  placeStyle,
  iconStyle,
}) {
  const mode = getAddressDisplayMode(layoutConfig, defaultMode);
  const alignment = getAddressAlignment(layoutConfig, defaultAlign);
  const textPosition = getAddressTextPosition(layoutConfig);
  const placePosition = getAddressPlacePosition(layoutConfig);
  const placeReference = getAddressPlaceReference(layoutConfig);
  const showPin = mode === "pin" || mode === "pinText";
  const showText = mode === "text" || mode === "pinText";
  const showPlace = getAddressPlaceVisible(layoutConfig) && !!nomeEspaco;
  const justifyPair = alignment === "justify" && showPin && showText && !isVertical(textPosition);
  const textAlign = getTextAlignValue(alignment);
  const justifyContent = getFlexJustifyValue(alignment, true);
  const pinBg = layoutConfig?.addrPinBg ?? defaultPinBg;
  const selectedPinBorderColor = layoutConfig?.addrPinBorderColor;
  const rawPinBorderMode = getAddressPinBorderMode(layoutConfig, defaultPinBorderMode);
  const pinBorderMode = rawPinBorderMode === "default" && selectedPinBorderColor ? "custom" : rawPinBorderMode;
  const pinBorder = pinBorderMode === "custom"
    ? (selectedPinBorderColor
      ? `2px solid ${selectedPinBorderColor}`
      : (defaultPinBorderColor ? `2px solid ${defaultPinBorderColor}` : "none"))
    : (pinBorderMode === "none" ? "none" : (defaultPinBorderColor ? `2px solid ${defaultPinBorderColor}` : "none"));

  let pinNode = showPin ? (
    <span
      aria-hidden="true"
      style={{
        width: pinSize,
        height: pinSize,
        borderRadius: pinRadius,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: pinBg,
        border: pinBorder,
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.18)",
        fontSize: iconSize,
        lineHeight: 1,
        flexShrink: 0,
        ...iconStyle,
      }}
    >
      📍
    </span>
  ) : null;

  let textNode = showText ? (
    <span
      style={{
        fontSize,
        fontWeight,
        lineHeight: 1.4,
        textAlign: justifyPair ? (textPosition === "left" ? "left" : "right") : textAlign,
        flex: justifyPair ? 1 : undefined,
        minWidth: 0,
        ...textStyle,
      }}
    >
      {endereco}
    </span>
  ) : null;

  const placeNode = showPlace ? (
    <span
      style={{
        fontSize,
        fontWeight: 800,
        lineHeight: 1.25,
        textAlign,
        minWidth: 0,
        ...placeStyle,
      }}
    >
      {nomeEspaco}
    </span>
  ) : null;

  if (placeNode && placeReference === "pin" && pinNode) {
    pinNode = makePositionedGroup(pinNode, placeNode, placePosition, Math.max(6, Math.round(gap / 2)));
  } else if (placeNode && placeReference === "text" && textNode) {
    textNode = makePositionedGroup(textNode, placeNode, placePosition, Math.max(6, Math.round(gap / 2)));
  }

  const addressPair = makePositionedGroup(
    pinNode,
    textNode,
    textPosition,
    gap,
    justifyPair ? { width: "100%", justifyContent: "space-between" } : undefined
  );
  const content = placeNode && (
    (placeReference === "block") ||
    (placeReference === "pin" && !pinNode) ||
    (placeReference === "text" && !textNode)
  )
    ? makePositionedGroup(addressPair, placeNode, placePosition, gap)
    : addressPair;

  if (!content) return null;

  return (
    <a
      href={mapsUrl}
      target="_blank"
      rel="noreferrer"
      title="Ver no mapa"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent,
        width: "100%",
        textDecoration: "none",
        color,
        textAlign,
        ...style,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: alignment === "justify" ? "100%" : undefined,
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {content}
      </span>
    </a>
  );
}
